import React, { useCallback, useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Button, IconButton, List, ListItem, Stack, Typography } from '@mui/material';

import { RevocableStandaloneToken } from '@paragon/contract-library';
import { userService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools/lib/actions/notificationActions';
import { Dialog } from '@paragon/popup-state';

import { generatePermanentLink } from '../../actions/permanentLinkActions';
import { useAppDispatch, useAppSelector } from '../hooks';
import { NewPermanentLink } from '../interface/NewPermanentLink';
import { PermanentLinksDialogProps } from './dialogs';

export function PermanentLinksDialog({ stationGuid }: PermanentLinksDialogProps) {
  const dispatch = useAppDispatch();
  const stations = useAppSelector((state) => state.stations.stations);
  const station = stations[stationGuid];

  const [standaloneTokens, setStandaloneTokens] = useState<
    readonly RevocableStandaloneToken[] | 'loading' | 'error' | null
  >(null);

  const [generatedToken, setGeneratedToken] = useState<{ readonly token: string; readonly guid: string } | null>(null);

  const [isWorking, setIsWorking] = useState(false);

  const loadStandaloneTokens = useCallback(() => {
    userService.tokens
      .getStandaloneTokensForStation(stationGuid)
      .then(setStandaloneTokens)
      .catch((error) => {
        console.error(error);
        setStandaloneTokens('error');
      });
  }, [stationGuid]);

  useEffect(() => {
    loadStandaloneTokens();
  }, [loadStandaloneTokens, stationGuid]);

  async function onRevokeStandaloneToken(tokenGuid: string) {
    try {
      setIsWorking(true);
      await userService.tokens.revokeStandaloneToken(tokenGuid);
      setStandaloneTokens((oldState) =>
        oldState && oldState !== 'loading' && oldState !== 'error'
          ? oldState.filter((token) => token.guid !== tokenGuid)
          : oldState,
      );
      if (generatedToken?.guid === tokenGuid) {
        setGeneratedToken(null);
      }
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('There was an error revoking the permanent link. Please try again.'));
    } finally {
      setIsWorking(false);
    }
  }

  async function onRevokeAllStandaloneTokens() {
    if (standaloneTokens == null || standaloneTokens === 'loading' || standaloneTokens === 'error') return;

    try {
      setIsWorking(true);
      await Promise.all(standaloneTokens.map((token) => userService.tokens.revokeStandaloneToken(token.guid)));
      setStandaloneTokens([]);
      setGeneratedToken(null);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('There was an error revoking all permanent links. Please try again.'));
    } finally {
      setIsWorking(false);
    }
  }

  async function onGeneratePermanentLink() {
    setIsWorking(true);
    const response = await dispatch(generatePermanentLink(stationGuid));
    if (response) {
      setGeneratedToken(response);
      loadStandaloneTokens();
    }
    setIsWorking(false);
  }

  return (
    <Dialog title={`Permanent Links for ${station.name}`} submitText="Close" hasSpinner={isWorking}>
      <Button
        variant="outlined"
        onClick={onGeneratePermanentLink}
        disabled={isWorking || !!generatedToken}
        sx={{ mb: 2 }}
      >
        Get a permanent link
      </Button>
      {generatedToken && <NewPermanentLink stationGuid={stationGuid} token={generatedToken.token} />}
      {standaloneTokens === 'loading' ? null : standaloneTokens === 'error' ? (
        <Alert severity="error">Failed to load permanent links.</Alert>
      ) : !standaloneTokens || standaloneTokens.length === 0 ? (
        <Typography>No existing permanent links found.</Typography>
      ) : (
        <Stack spacing={1}>
          <Typography variant="h6">Existing Permanent Links</Typography>
          <List>
            {standaloneTokens.map((token) => (
              <ListItem
                key={token.guid}
                secondaryAction={
                  <IconButton onClick={() => onRevokeStandaloneToken(token.guid)} edge="end" disabled={isWorking}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                Created by {token.createdBy} on {new Date(token.createdAt).toLocaleString()}
              </ListItem>
            ))}
          </List>
          <Button
            variant="outlined"
            onClick={onRevokeAllStandaloneTokens}
            disabled={isWorking || standaloneTokens.length === 0}
          >
            Revoke All
          </Button>
        </Stack>
      )}
    </Dialog>
  );
}
